<script lang="ts">
    import { onMount } from 'svelte';
    import { api } from '../lib/api';
    import { toast } from '../lib/stores/toast.svelte';
    import type { Backup, BackupListResponse, RestoreJobResponse, RestoreStatusResponse } from '../lib/types';
    import { formatDate, formatBytes } from '../lib/format';

    let items = $state<Backup[]>([]);
    let s3Configured = $state(false);
    let s3Reconciled = $state(false);
    let orphanedS3Keys = $state<string[]>([]);
    let loading = $state(true);

    // Restore dialog state
    let restoreTarget = $state<Backup | null>(null);
    let restoreConfirmText = $state('');
    let restoreJobId = $state<string | null>(null);
    let restoreState = $state<string | null>(null);
    let restoreError = $state<string | null>(null);
    let restorePollTimer: ReturnType<typeof setInterval> | null = null;

    async function load() {
        loading = true;
        try {
            const res = await api<BackupListResponse>('/api/backups');
            items = res.items;
            s3Configured = res.s3Configured;
            s3Reconciled = res.s3Reconciled;
            orphanedS3Keys = res.orphanedS3Keys;
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            loading = false;
        }
    }
    onMount(load);

    async function runBackup() {
        try {
            await api('/api/backups/run', { method: 'POST' });
            toast.info('Backup queued — refresh in a moment');
            setTimeout(load, 2500);
        } catch (err: any) {
            toast.error(err.message);
        }
    }

    function openRestoreDialog(backup: Backup) {
        restoreTarget = backup;
        restoreConfirmText = '';
        restoreJobId = null;
        restoreState = null;
        restoreError = null;
    }

    function closeRestoreDialog() {
        restoreTarget = null;
        restoreConfirmText = '';
        restoreJobId = null;
        restoreState = null;
        restoreError = null;
        if (restorePollTimer) {
            clearInterval(restorePollTimer);
            restorePollTimer = null;
        }
    }

    async function confirmRestore() {
        if (!restoreTarget) return;
        try {
            const res = await api<RestoreJobResponse>(`/api/backups/${restoreTarget.id}/restore`, {
                method: 'POST',
            });
            restoreJobId = res.jobId;
            restoreState = 'waiting';
            toast.info('Restore queued — database will be overwritten');

            // Start polling
            restorePollTimer = setInterval(pollRestoreStatus, 2000);
        } catch (err: any) {
            toast.error(err.message);
            restoreError = err.message;
        }
    }

    async function pollRestoreStatus() {
        if (!restoreJobId) return;
        try {
            const status = await api<RestoreStatusResponse>(`/api/backups/restore-status/${restoreJobId}`);
            restoreState = status.state;

            if (status.state === 'completed') {
                if (restorePollTimer) {
                    clearInterval(restorePollTimer);
                    restorePollTimer = null;
                }
                toast.success('Restore completed successfully');
                setTimeout(() => {
                    closeRestoreDialog();
                    load();
                }, 1500);
            } else if (status.state === 'failed') {
                if (restorePollTimer) {
                    clearInterval(restorePollTimer);
                    restorePollTimer = null;
                }
                restoreError = status.failedReason || 'Restore failed';
                toast.error('Restore failed — check audit logs');
            }
        } catch (err: any) {
            // Ignore polling errors — will retry on next interval
        }
    }

    async function del(id: string) {
        if (!confirm('Delete this backup from S3 and the ledger?')) return;
        try {
            await api(`/api/backups/${id}`, { method: 'DELETE' });
            load();
        } catch (err: any) {
            toast.error(err.message);
        }
    }

    function statusBadgeClass(status: string): string {
        switch (status) {
            case 'SUCCESS': return 'success';
            case 'FAILED': return 'danger';
            case 'IN_PROGRESS': return 'warning';
            case 'RESTORING': return 'warning';
            default: return 'info';
        }
    }
</script>

<div class="page-header">
    <h1>Backups</h1>
    <div class="toolbar">
        <button onclick={load}>Refresh</button>
        <button class="primary" onclick={runBackup} disabled={!s3Configured}>Run Backup Now</button>
    </div>
</div>

{#if !s3Configured}
    <div class="card warning-box">
        ⚠️ S3 is not configured. Backups and restores are disabled.
        Set S3_ENDPOINT, S3_BUCKET, S3_ACCESS_KEY_ID, and S3_SECRET_ACCESS_KEY to enable.
    </div>
{/if}

{#if s3Reconciled && orphanedS3Keys.length > 0}
    <div class="card" style="border-left: 3px solid var(--warning, orange);">
        <strong>⚠️ S3 Reconciliation:</strong> Found {orphanedS3Keys.length} orphaned object(s) in S3
        not tracked in the database ledger:
        <ul class="mono small" style="margin-top: 8px;">
            {#each orphanedS3Keys.slice(0, 10) as key}
                <li>{key}</li>
            {/each}
            {#if orphanedS3Keys.length > 10}
                <li>... and {orphanedS3Keys.length - 10} more</li>
            {/if}
        </ul>
    </div>
{/if}

{#if loading}
    <p class="muted">Loading...</p>
{:else}
    <div class="card">
        <table>
            <thead>
                <tr>
                    <th>Created</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Size</th>
                    <th>Key Ver</th>
                    <th>S3</th>
                    <th>S3 Key</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {#each items as b}
                    <tr>
                        <td>{formatDate(b.createdAt)}</td>
                        <td>
                            <span class="badge {b.type === 'MANUAL' ? 'info' : 'success'}">{b.type}</span>
                        </td>
                        <td>
                            <span class="badge {statusBadgeClass(b.status)}">{b.status}</span>
                            {#if b.errorMsg}
                                <div class="dim small">{b.errorMsg}</div>
                            {/if}
                        </td>
                        <td>{formatBytes(b.sizeBytes)}</td>
                        <td>v{b.keyVersion}</td>
                        <td>
                            {#if b.s3Exists === true}
                                <span style="color: green;">✓</span>
                            {:else if b.s3Exists === false}
                                <span style="color: red;" title="Object missing from S3">✗</span>
                            {:else}
                                <span class="dim">—</span>
                            {/if}
                        </td>
                        <td class="mono small">{b.s3ObjectKey}</td>
                        <td class="actions">
                            {#if b.status === 'SUCCESS'}
                                <button
                                    class="ghost"
                                    onclick={() => openRestoreDialog(b)}
                                    disabled={restoreTarget !== null}
                                >Restore…</button>
                            {/if}
                            <button class="ghost" onclick={() => del(b.id)}>🗑</button>
                        </td>
                    </tr>
                {:else}
                    <tr>
                        <td colspan="8" class="muted" style="text-align:center;padding:20px;">
                            No backups yet.
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
{/if}

{#if restoreTarget}
    <dialog open>
        <header>
            <h2>
                {#if restoreState === 'completed'}
                    Restore Complete
                {:else if restoreState === 'failed'}
                    Restore Failed
                {:else if restoreJobId}
                    Restore In Progress
                {:else}
                    Confirm Restore
                {/if}
            </h2>
        </header>
        <div class="body">
            {#if !restoreJobId}
                <!-- Confirmation phase -->
                <div class="warning-box" style="margin-bottom: 16px;">
                    ⚠️ <strong>WARNING:</strong> This will permanently destroy all current data
                    and replace it with the backup from <strong>{formatDate(restoreTarget.createdAt)}</strong>.
                    <br><br>
                    This operation:
                    <ul>
                        <li>Drops the entire <code>public</code> schema (CASCADE)</li>
                        <li>Restores all tables, functions, and data from the backup</li>
                        <li>Cannot be undone</li>
                    </ul>
                </div>

                <table class="kv-table">
                    <tbody>
                        <tr><td>Backup ID:</td><td class="mono">{restoreTarget.id}</td></tr>
                        <tr><td>Created:</td><td>{formatDate(restoreTarget.createdAt)}</td></tr>
                        <tr><td>Size:</td><td>{formatBytes(restoreTarget.sizeBytes)}</td></tr>
                        <tr><td>Key Version:</td><td>v{restoreTarget.keyVersion}</td></tr>
                        <tr><td>S3 Key:</td><td class="mono small">{restoreTarget.s3ObjectKey}</td></tr>
                    </tbody>
                </table>

                <p style="margin-top: 16px;">
                    Type <strong>RESTORE</strong> to confirm:
                </p>
                <input
                    type="text"
                    bind:value={restoreConfirmText}
                    placeholder="RESTORE"
                    style="width: 100%; padding: 8px; font-family: monospace;"
                />
            {:else if restoreState === 'completed'}
                <div class="success-box" style="padding: 16px;">
                    ✅ Database restored successfully from backup created on {formatDate(restoreTarget.createdAt)}.
                </div>
            {:else if restoreState === 'failed'}
                <div class="warning-box" style="padding: 16px;">
                    ❌ Restore failed: {restoreError}
                    <br><br>
                    The database may be in an inconsistent state. Check the audit log for details.
                    You may need to attempt another restore or recover manually.
                </div>
            {:else}
                <!-- In progress -->
                <div style="text-align: center; padding: 24px;">
                    <div class="spinner" style="margin: 0 auto 16px;"></div>
                    <p>Restoring database from backup created on {formatDate(restoreTarget.createdAt)}...</p>
                    <p class="dim small">Job ID: {restoreJobId} · State: {restoreState}</p>
                    <p class="dim small">Do not close this page or restart the backend.</p>
                </div>
            {/if}
        </div>
        <footer>
            {#if restoreState === 'completed'}
                <button class="primary" onclick={closeRestoreDialog}>Close</button>
            {:else if restoreState === 'failed'}
                <button onclick={closeRestoreDialog}>Close</button>
            {:else if restoreJobId}
                <button disabled>Restore in progress...</button>
            {:else}
                <button onclick={closeRestoreDialog}>Cancel</button>
                <button
                    class="danger"
                    onclick={confirmRestore}
                    disabled={restoreConfirmText !== 'RESTORE'}
                >Confirm Restore</button>
            {/if}
        </footer>
    </dialog>
{/if}

<style>
    .kv-table {
        width: 100%;
        border-collapse: collapse;
    }
    .kv-table td {
        padding: 4px 8px;
        border-bottom: 1px solid var(--border, #eee);
    }
    .kv-table td:first-child {
        font-weight: 600;
        width: 120px;
    }
    .spinner {
        width: 32px;
        height: 32px;
        border: 3px solid #ccc;
        border-top-color: var(--primary, #007bff);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    .danger {
        background: var(--danger, #dc3545);
        color: white;
    }
    .danger:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    .success-box {
        background: rgba(40, 167, 69, 0.1);
        border: 1px solid rgba(40, 167, 69, 0.3);
        border-radius: 4px;
    }
</style>